import { activateTab, TAB_NAV_EVENT, updateUrl } from "../Tabbed";
import { AbstractFormElement } from "../../Components/Form/Abstract";
import { Checkbox } from "../../Components/Form/Checkbox";
import { type ICompletedStep, type IncomingPurchaseRequest, type IPurchase, type IStepInfo, postRequest } from "./Data";
import { Toast } from "../../Components/Dialog/Toast";
import { InputText } from "../../Components/Form/Input";
import { formatThousandSeparator } from "../../Utils/Number";
import LoaderOverlay from "../LoaderOverlay";
import Confetti from "../RentalAndAppointments/Confetti";
import { SELECTORS_FORM_SALE as SELECTORS } from "./Constants";
import FilePond, { FilePondCustomEvent } from "./FilePond";

class Form {
    CONFETTI_OPTIONS = {
        count: 200,
        defaults: {
            origin: { y: 0.7 }
        }
    };

    static LOCAL_STORAGE_KEY = "purchase-form";

    private readonly form: HTMLFormElement;
    private backBtn: HTMLButtonElement;
    private nextBtn: HTMLButtonElement;
    private submitBtn: HTMLButtonElement;

    private state: IPurchase;
    private images: Set<string>;
    private thumbnails: Set<string>;
    private heroes: NodeListOf<HTMLElement>;
    private activeTabId: string;
    private openRequestsBox: HTMLElement;

    private forms: NodeListOf<HTMLFormElement>;
    private tabs = document.querySelectorAll(SELECTORS.tabs) as NodeListOf<HTMLElement>;
    private steps = document.querySelectorAll(SELECTORS.step) as NodeListOf<HTMLElement>;
    private readonly pond: FilePond;
    private readonly confetti = new Confetti(this.CONFETTI_OPTIONS.count, this.CONFETTI_OPTIONS.defaults);
    private _keepImages = true;

    private readonly imageHandler: (event: FilePondCustomEvent) => void;

    constructor(form: HTMLFormElement, forms: NodeListOf<HTMLFormElement>) {
        this.forms = forms;
        this.form = form;
        this.backBtn = this.form.querySelector(SELECTORS.back);
        this.nextBtn = this.form.querySelector(SELECTORS.next);
        this.submitBtn = this.form.querySelector(SELECTORS.submit);
        this.state = JSON.parse(localStorage.getItem(Form.LOCAL_STORAGE_KEY)) || {};
        this.images = new Set();
        this.thumbnails = new Set();
        this.heroes = document.querySelectorAll(SELECTORS.hero) as NodeListOf<HTMLElement>;
        if (this.form.querySelector("input.filepond")) {
            this.pond = new FilePond(this.form.querySelector("input.filepond"));
            this.imageHandler = this.handleImages.bind(this);
        }
        const activeTab = Array.from(this.tabs).filter((tab) => tab.classList.contains("active"))[0];
        this.activeTabId = activeTab?.dataset.tab;
        this.openRequestsBox = document.querySelector(SELECTORS.openRequest);
        this.setActiveHero(this.activeTabId);

        if (Object.keys(this.state).length) {
            this.setFormValues(this.form, this.state);
        }
        if (this.state.step) {
            this.setLastStepInfo(this.state.step as IStepInfo);
        }
        this.initListeners();
    }

    initListeners() {
        this.backBtn?.addEventListener("click", (e) => this.back(e));
        this.nextBtn?.addEventListener("click", (e) => this.next(e));
        if (this.form.classList.contains("last")) {
            this.submitBtn?.addEventListener("click", (e) => this.submit(e));
        }
        this.openRequestsBox.querySelector(".remove").addEventListener("click", () => {
            this.openRequestsBox.classList.add("hide-me");
            this.resetForms();
        });

        this.steps.forEach((tab) =>
            tab.addEventListener(TAB_NAV_EVENT, () => this.setActiveHero(tab.id.replace("tab-", "")))
        );

        if (this.pond) {
            document.addEventListener("FilePond:init", (e: CustomEvent) => {
                const { images } = this.state;
                e.detail.pond.setOptions({
                    files: images
                });
            });
            document.addEventListener("FilePond:addFile", this.imageHandler);
            document.addEventListener("FilePond:deleteFile", this.imageHandler);
            document.addEventListener("FilePond:reset", () => {
                this.images = new Set();
                this.thumbnails = new Set();
            });
        }

        for (const element in Array.from(this.form.elements)) {
            const formElement = this.form.elements[element] as AbstractFormElement;

            if (formElement instanceof FilePond) {
                continue;
            }

            if (formElement instanceof InputText && formElement.inputMode === "decimal") {
                formElement.addEventListener("input", () => formatThousandSeparator(formElement));
            }
            if (formElement instanceof Checkbox) {
                formElement.addEventListener("change", () => {
                    formElement.value = formElement.checked ? "1" : "0";
                    const formData = this.getFormData(this.form);
                    formData.set(formElement.name, formElement.value);
                    this.saveState(formData);
                });
            }
            formElement.addEventListener("change", () => {
                this.saveState(this.getFormData(this.form));
            });
            formElement.addEventListener("input", () => {
                this.saveState(this.getFormData(this.form));
            });
        }

        return this;
    }

    back(e: MouseEvent) {
        e.preventDefault();

        const activeTab = document.querySelector(SELECTORS.tabs + ".active") as HTMLElement;
        const previousTab = activeTab?.previousElementSibling as HTMLElement;
        const previousTabId = previousTab?.dataset.tab;

        this.jumpToTab(previousTabId);
    }

    next(e: MouseEvent) {
        e.preventDefault();
        const activeTab = document.querySelector(SELECTORS.tabs + ".active") as HTMLElement;
        const nextTab = activeTab?.nextElementSibling as HTMLElement;
        const nextTabId = nextTab?.dataset.tab;
        const inputs = this.form.elements;

        const formData = this.getFormData(this.form);

        if (this.form.checkValidity()) {
            this.jumpToTab(nextTabId);
            if (activeTab.dataset.tab !== "images") {
                activeTab.classList.add("completed");
            }

            for (let i = 0; i < inputs.length; i++) {
                const input = inputs[i] as AbstractFormElement;
                const target = input.dataset.showThis;
                if (this.pond) {
                    this.images = this.pond.images;
                    this.thumbnails = this.pond.thumbnails;
                    activeTab.classList.add("completed");
                }
                if (input instanceof InputText && input.inputMode === "decimal") {
                    formData.set(input.name, input.value.replace(/\./g, ""));
                }
                if (input instanceof Checkbox && !input.checked) {
                    formData.set(input.name, "0");
                    if (target) {
                        formData.set(target, "");
                    }
                }
            }

            this.saveState(formData);
        }
        this.form.dispatchEvent(new CustomEvent("form:next"));
    }

    async submit(e: Event) {
        e.preventDefault();

        const formData = this.getFormData(this.form);
        const state = JSON.parse(localStorage.getItem(Form.LOCAL_STORAGE_KEY));

        if (this.checkAllFormsValidity() && this.form.checkValidity()) {
            const loaderOverlay = new LoaderOverlay();
            // Update request without saving personal data to local storage
            this.state.request = {
                ...state.request,
                ...Object.fromEntries(formData)
            };

            const formattedState = {
                ...this.state,
                request: Form.prepareRequest(this.state.request)
            };

            try {
                loaderOverlay.render();
                const response = await postRequest(formattedState);

                if (response.success) {
                    cancel(this.form, e);
                    new Toast({
                        title: window.SuccessMessages.purchaseRequest.success,
                        type: Toast.TYPE_SUCCESS
                    })?.render();

                    await this.confetti.render();
                    this.resetForms();
                } else {
                    const errorMessage = `${window.SuccessMessages.purchaseRequest.error} (Error code ${response.errorCode})`;
                    console.error(new Error(errorMessage));
                    new Toast({
                        title: errorMessage,
                        type: Toast.TYPE_ERROR
                    })?.render();
                }
            } catch (e) {
                console.error(e);
                new Toast({
                    title: window.SuccessMessages.purchaseRequest.error,
                    type: Toast.TYPE_ERROR
                })?.render();
            } finally {
                loaderOverlay.destroy();
            }
        }

        this.form.dispatchEvent(new CustomEvent("form:submit"));
    }

    private saveState(formData: FormData) {
        const formDataObject = Object.fromEntries(formData);
        const localStorageData = JSON.parse(localStorage.getItem(Form.LOCAL_STORAGE_KEY)) || {
            request: {},
            step: {},
            images: [],
            thumbnails: [],
            completed: {}
        };
        const stepInfo = this.getStepsCount();
        const tabId = this.form.closest(".step").id.replace("tab-", "") as keyof ICompletedStep;
        const completed: ICompletedStep = tabId && {
            ...localStorageData.completed,
            [tabId]: this.form.checkValidity()
        };

        if (!this.images.size && this.keepImages) {
            this.images = new Set(localStorageData.images);
            this.thumbnails = new Set(localStorageData.thumbnails);
        }

        this.state = {
            ...localStorageData,
            step: stepInfo,
            images: [...this.images],
            thumbnails: [...this.thumbnails],
            request: { ...localStorageData.request, ...formDataObject },
            completed
        };

        localStorage.setItem(Form.LOCAL_STORAGE_KEY, JSON.stringify(this.state));
        this.setCompleteSteps();
        this.setLastStepInfo(stepInfo);
        this.keepImages = true;
    }

    static prepareRequest(request: IncomingPurchaseRequest): IncomingPurchaseRequest {
        switch (+request.front_tire) {
            case 0:
                request.front_tire = 40;
                break;
            case 1:
                request.front_tire = 50;
                break;
            case 2:
                request.front_tire = 75;
                break;
            case 3:
                request.front_tire = 100;
                break;
            default:
                request.front_tire = 0;
        }
        switch (+request.rear_tire) {
            case 0:
                request.rear_tire = 40;
                break;
            case 1:
                request.rear_tire = 50;
                break;
            case 2:
                request.rear_tire = 75;
                break;
            case 3:
                request.rear_tire = 100;
                break;
            default:
                request.rear_tire = 0;
        }
        if (request.vb === "4") {
            request.vb = "4+";
        }
        request.chain = Number(request.chain);
        request.keys = Number(request.keys);
        if (request.tire_age) {
            request.tire_age = Boolean(+request.tire_age) || null;
        }
        if (request.pickup) {
            request.pickup = Boolean(+request.pickup) || null;
        }
        if (request.fgnr) {
            request.fgnr = +request.fgnr;
        }
        if (request.tüv) {
            const tuv = request.tüv.split("-");
            if (tuv.length < 3) {
                request.tüv = `${tuv[0]}-${tuv[1]}-01`;
            }
        }
        if (request.kd_km) {
            request.kd_km = Number(String(request.kd_km).replace(".", ""));
        }
        if (request.km) {
            request.km = Number(String(request.km).replace(".", ""));
        }
        return {
            brand: request.brand,
            model: request.model,
            color: request.color,
            vb: request.vb,
            tüv: request.tüv,
            crash: request.crash || null,
            front_tire: request.front_tire,
            rear_tire: request.rear_tire,
            chain: request.chain,
            tire_age: request.tire_age || null,
            keys: request.keys,
            kd_km: request.kd_km || null,
            kd_date: request.kd_date || null,
            assesment: request.assesment || null,
            mail: request.mail,
            tel: request.tel || null,
            available_from: request.available_from || null,
            available_until: request.available_until || null,
            name: request.name,
            street: request.street,
            city: request.city,
            plz: request.plz,
            features: request.features || null,
            repairs: request.repairs || null,
            buying_interest_brand: request.buying_interest_brand || null,
            buying_interest_model: request.buying_interest_model || null,
            buying_interest_internal: request.buying_interest_internal || null,
            pv: request.pv || null,
            pickup: request.pickup || null,
            ez: request.ez,
            first_name: request.first_name || null,
            title: request.title || null,
            company: request.company || null,
            fgnr: request.fgnr || null,
            km: request.km,
            ap: request.ap || null,
            ap_team: request.ap_team || null
        };
    }

    private getFormData(form: HTMLFormElement) {
        return new FormData(form);
    }

    private setFormValues(form: HTMLFormElement, data: IPurchase) {
        const requestData = data.request;
        const images = data.images;
        for (const [key, value] of Object.entries(requestData)) {
            const element = form.querySelector(`[name="${key}"]`) as AbstractFormElement | Checkbox;
            if (element && value) {
                element.value = value;
                element.validate();
                element.dispatchEvent(new Event("input"));
                if (element instanceof Checkbox && value === "1") {
                    element.checked = true;
                    element.dispatchEvent(new Event("change"));
                }
                if (element instanceof InputText && element.inputMode === "decimal") {
                    formatThousandSeparator(element);
                }
            }
        }

        if (this.pond) {
            this.pond.images = new Set(images);
        }

        Form.setSalesPerson();

        if (form.checkValidity()) {
            this.setCompleteSteps();
        }
    }

    private resetForms() {
        const firstTab = this.tabs.item(0).dataset.tab;
        this.forms?.forEach((form) => form.reset());
        this.jumpToTab(firstTab);
        this.tabs?.forEach((tab) => tab.classList.remove("completed"));
        document.dispatchEvent(new CustomEvent("FilePond:reset"));
        this.form.dispatchEvent(new CustomEvent("form:reset"));
        this.openRequestsBox.classList.add("hide-me");
        localStorage.removeItem(Form.LOCAL_STORAGE_KEY);
        location.reload();
    }

    private getStepsCount(): IStepInfo {
        const steps = document.querySelector(SELECTORS.formContainer).querySelectorAll(SELECTORS.step);
        const last = Number(this.form.id.replace("step-", ""));
        const lastEdited = Date.now();
        return {
            lastEdited,
            last,
            total: steps.length
        };
    }

    private setLastStepInfo(stepInfo: IStepInfo): void {
        const { last, lastEdited, total } = stepInfo;
        this.openRequestsBox.classList.remove("hide-me");
        const removeButton = this.openRequestsBox.querySelector(".remove");
        const title = this.openRequestsBox.querySelector(".model");
        const lastEdit = this.openRequestsBox.querySelector(".last-edit");
        const brand = this.state.request?.brand || "";
        const model = this.state.request?.model || "";
        const lastEditDate = new Date(lastEdited).toLocaleString("de-DE").split(",")[0];
        const progressBar = this.openRequestsBox.querySelector(".progress");
        const progress = (last / total) * 100;

        title.innerHTML = `${brand} ${model}`;
        progressBar.setAttribute("style", `--open-request-progress: ${progress}%`);
        this.openRequestsBox.querySelector(".last").innerHTML = last.toString();
        this.openRequestsBox.querySelector(".total").innerHTML = total.toString();
        lastEdit.querySelector(".date").innerHTML = lastEditDate;

        this.openRequestsBox.addEventListener("click", (e) => {
            if (e.target === removeButton || removeButton.contains(e.target as Node)) {
                return;
            }
            const formContainer = document.querySelector(SELECTORS.formContainer);
            const tab = formContainer.querySelector(`#step-${last}`).closest(".step") as HTMLElement;
            const tabId = tab?.id.replace("tab-", "");
            formContainer.parentElement.classList.add("revealed");
            formContainer.closest("main.sale").classList.add("has-revealed");
            this.jumpToTab(tabId);
        });
    }

    private setCompleteSteps(): void {
        const { completed } = this.state;

        if (completed) {
            for (const [tabId, complete] of Object.entries(completed)) {
                const tab = document.querySelector(`span[data-tab=${tabId}]`);
                if (!complete) {
                    tab?.classList.remove("completed");
                } else {
                    tab?.classList.add("completed");
                }
            }
        }
    }

    static setSalesPerson(): void {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const ap = urlParams.get("ap");
        let apTeam = urlParams.get("ap_team");

        if (ap) {
            const apElement = document.getElementById("ap") as HTMLInputElement;
            apElement.value = ap;
            apElement.dispatchEvent(new Event("input"));
        }

        if (apTeam) {
            if (!apTeam.startsWith("Team")) {
                apTeam = `Team ${apTeam}`;
            }
            const apTeamElement = document.getElementById("ap_team") as HTMLInputElement;
            apTeamElement.value = apTeam;
            apTeamElement.dispatchEvent(new Event("input"));
        }
    }

    private checkAllFormsValidity(): boolean {
        const forms = document.querySelectorAll(SELECTORS.form) as NodeListOf<HTMLFormElement>;
        const tabs = document.querySelectorAll(SELECTORS.tabs + ":not(.completed)") as NodeListOf<HTMLElement>;
        let invalidForm;
        let incompleteTab;
        for (const form of forms) {
            if (!form.checkValidity()) {
                invalidForm = form;
                break;
            }
        }
        if (tabs.length) {
            incompleteTab = tabs[0].dataset.tab;
        }
        const tabId = incompleteTab || invalidForm?.closest(".step")?.id.replace("tab-", "");
        if (tabId && tabId !== "personal-data") {
            invalidForm?.dispatchEvent(new CustomEvent("form:next"));
            this.jumpToTab(tabId);
        }
        return !tabId;
    }

    private jumpToTab(tabId: string): void {
        const headerHeight = document.querySelector<HTMLElement>("body > header").offsetHeight;
        const top = document.querySelector("section.steps").getBoundingClientRect().top + window.scrollY - headerHeight;
        window.scrollTo({ behavior: "smooth", top: top, left: 0 });
        this.activeTabId = tabId;
        activateTab(tabId);
        updateUrl(tabId);
        this.setActiveHero(tabId);
    }

    private setActiveHero(tabId: string): void {
        const activeHero = Array.from(this.heroes).find((hero) => hero.dataset.idx === tabId);
        if (activeHero) {
            this.heroes.forEach((hero) => hero.classList.remove("active"));
            activeHero.classList.add("active");
        }
    }

    private handleImages(event: FilePondCustomEvent) {
        const urls = event.detail;
        this.images = urls.images;
        this.thumbnails = urls.thumbnails;
        if (event.type === "FilePond:deleteFile") {
            this.keepImages = false;
        }
        this.saveState(this.getFormData(this.form));
    }

    get keepImages(): boolean {
        return this._keepImages;
    }

    set keepImages(value: boolean) {
        this._keepImages = value;
    }
}

export default Form;

const cancel = (formContainer: HTMLElement, e: Event) => {
    e.preventDefault();
    formContainer.closest(".revealed").classList.remove("revealed");
    formContainer.closest(".has-revealed").classList.remove("has-revealed");
};

const revealBtns = document.querySelectorAll(SELECTORS.reveal) as NodeListOf<HTMLElement>;
revealBtns.forEach((revealBtn) => {
    const formContainer = document.getElementById(revealBtn.dataset.reveal) as HTMLElement;
    if (formContainer) {
        const cancelBtns = formContainer.querySelectorAll<HTMLButtonElement>(SELECTORS.cancel);
        revealBtn.addEventListener("click", () => {
            window.scrollTo({ behavior: "smooth", top: 0, left: 0 });
            formContainer.parentElement.classList.add("revealed");
            formContainer.closest("main.sale").classList.add("has-revealed");
            Form.setSalesPerson();
        });
        cancelBtns.forEach((cancelBtn) => cancelBtn.addEventListener("click", cancel.bind(this, formContainer)));
    }
});

const saleSteps = document.getElementById("sale-form") as HTMLElement;
const forms = saleSteps?.querySelectorAll(SELECTORS.form) as NodeListOf<HTMLFormElement>;
if (forms && forms.length > 0) {
    forms.forEach((form) => new Form(form, forms));
}
